import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader2, CheckCircle2, Camera, ArrowRight } from 'lucide-react';
import PhoneField, { isPhoneValid } from '../PhoneField';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ConsultationModal — Gabrielle's "Book an art consultation" dialog,
 * lifted out of `ArtConsultationPage.jsx` to keep the page under 600 lines.
 * Behaviour and DOM are identical to the original inline component.
 */
const ConsultationModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', style: '', room: '' });
  const [photo, setPhoto] = useState(null); // File
  const [photoPreview, setPhotoPreview] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState('');

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handlePhoto = (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      setErrorMsg('Please choose an image file (JPG, PNG, HEIC).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErrorMsg('Photo too large (10MB max).');
      return;
    }
    setErrorMsg('');
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const removePhoto = () => {
    setPhoto(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!isPhoneValid(form.phone)) {
      setErrorMsg('Please enter a valid phone number with country code.');
      return;
    }
    setStatus('sending');
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('email', form.email);
      fd.append('phone', form.phone || '');
      fd.append('style', form.style);
      fd.append('room', form.room);
      if (photo) fd.append('file', photo);

      const res = await fetch(`${API_URL}/api/consultation/upload-room`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Submission failed');
      }
      setStatus('sent');
      setForm({ name: '', email: '', phone: '', style: '', room: '' });
      removePhoto();
    } catch (err) {
      setErrorMsg(err.message || 'Something went wrong');
      setStatus('error');
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-xl p-8 sm:p-10 relative max-h-[92vh] overflow-y-auto"
        data-testid="consultation-modal"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 p-2 text-neutral-500 hover:text-black transition-colors"
          data-testid="consultation-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {status === 'sent' ? (
          <div className="text-center py-8">
            <CheckCircle2 className="w-12 h-12 text-black mx-auto mb-4" />
            <h3 className="font-serif text-2xl mb-3">Request received</h3>
            <p className="text-sm text-neutral-600 leading-relaxed">
              A specialist will be in touch within 24 hours to arrange your complimentary
              consultation.
            </p>
            <button
              onClick={onClose}
              className="mt-6 px-6 py-2.5 border border-black text-xs uppercase tracking-[0.25em] hover:bg-black hover:text-white transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
              Free art consultation
            </p>
            <h3 className="font-serif text-2xl leading-tight mb-2">
              Tell us about your space
            </h3>
            <p className="text-sm text-neutral-600 leading-relaxed mb-6">
              A complimentary discovery session. We reply within 24 hours.
            </p>

            <form onSubmit={submit} className="space-y-4" data-testid="consultation-form">
              <input
                type="text"
                required
                placeholder="Full name"
                value={form.name}
                onChange={update('name')}
                className="w-full px-0 py-3 border-0 border-b border-neutral-300 focus:border-black focus:outline-none bg-transparent text-sm placeholder:text-neutral-400"
                data-testid="consultation-name"
              />
              <input
                type="email"
                required
                placeholder="Email address"
                value={form.email}
                onChange={update('email')}
                className="w-full px-0 py-3 border-0 border-b border-neutral-300 focus:border-black focus:outline-none bg-transparent text-sm placeholder:text-neutral-400"
                data-testid="consultation-email"
              />
              <PhoneField
                value={form.phone}
                onChange={(v) => setForm((f) => ({ ...f, phone: v || '' }))}
                required
                placeholder="Phone number"
                testIdPrefix="consultation-phone"
              />
              <select
                required
                value={form.style}
                onChange={update('style')}
                className="w-full px-0 py-3 border-0 border-b border-neutral-300 focus:border-black focus:outline-none bg-transparent text-sm text-neutral-700"
                data-testid="consultation-style"
              >
                <option value="">Preferred interior style…</option>
                <option>Contemporary</option>
                <option>Classic</option>
                <option>Eclectic</option>
                <option>Minimalist</option>
                <option>Not sure — advise me</option>
              </select>
              <textarea
                rows={3}
                placeholder="Briefly describe your room (colours, furniture, light)"
                value={form.room}
                onChange={update('room')}
                className="w-full px-0 py-3 border-0 border-b border-neutral-300 focus:border-black focus:outline-none bg-transparent text-sm placeholder:text-neutral-400 resize-none"
                data-testid="consultation-room"
              />

              {/* Optional room photo upload */}
              <div className="pt-2">
                {!photoPreview ? (
                  <label
                    htmlFor="consultation-photo-input"
                    className="flex items-center gap-3 border border-dashed border-neutral-300 hover:border-black transition-colors p-4 cursor-pointer text-sm text-neutral-600"
                    data-testid="consultation-photo-dropzone"
                  >
                    <Camera className="w-5 h-5 text-neutral-500 shrink-0" strokeWidth={1.4} />
                    <div className="flex-1">
                      <p className="font-medium text-black">Add a photo of your room</p>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        Optional — sent securely to our team. JPG, PNG or HEIC, 10MB max.
                      </p>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 hidden sm:inline">
                      Choose
                    </span>
                    <input
                      id="consultation-photo-input"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => handlePhoto(e.target.files?.[0])}
                      data-testid="consultation-photo-input"
                    />
                  </label>
                ) : (
                  <div className="flex items-center gap-3 border border-neutral-200 p-3" data-testid="consultation-photo-preview">
                    <div className="w-16 h-16 overflow-hidden bg-neutral-100 shrink-0">
                      <img src={photoPreview} alt="Room preview" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-black truncate">{photo?.name || 'Room photo'}</p>
                      <p className="text-xs text-neutral-500">
                        {photo ? `${(photo.size / 1024 / 1024).toFixed(1)}MB` : ''} · ready to send
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={removePhoto}
                      className="text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-black p-2"
                      data-testid="consultation-photo-remove"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="pt-3">
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="px-8 py-3 bg-black text-white text-xs uppercase tracking-[0.25em] hover:bg-neutral-800 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-3"
                  data-testid="consultation-submit"
                >
                  {status === 'sending' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send request <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
              {status === 'error' && errorMsg && (
                <p className="text-xs text-red-600 text-center pt-2" data-testid="consultation-error">
                  {errorMsg}
                </p>
              )}
              {!errorMsg && status === 'error' && (
                <p className="text-xs text-red-600 text-center pt-2">
                  Something went wrong. Please email info@zebraonegallery.com directly.
                </p>
              )}
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default ConsultationModal;
